import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Truck, Plus, Pencil, Trash2, Search, RefreshCw, Briefcase, Users, ClipboardList, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CustomUnitsPage() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    unit_number: '',
    description: '',
    owner: 'Rogers'
  });

  const fetchUnits = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const response = await axios.get(`${API}/dispatch/custom-units?active_only=${!showInactive}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUnits(response.data.units);
      }
    } catch (error) {
      console.error('Failed to fetch units:', error);
      toast.error('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [showInactive]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('vaultai_token');
      
      if (editingUnit) {
        // Update existing unit
        const response = await axios.put(
          `${API}/dispatch/custom-units/${editingUnit.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success('Unit updated');
          setShowDialog(false);
          fetchUnits();
        } else {
          toast.error(response.data.error || 'Failed to update');
        }
      } else {
        // Create new unit
        const response = await axios.post(
          `${API}/dispatch/custom-units`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success('Unit created');
          setShowDialog(false);
          fetchUnits();
        } else {
          toast.error(response.data.error || 'Failed to create');
        }
      }
    } catch (error) {
      console.error('Failed to save unit:', error);
      toast.error('Failed to save unit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (unit) => {
    if (!window.confirm(`Deactivate unit ${unit.unit_number}?`)) return;
    
    try {
      const token = localStorage.getItem('vaultai_token');
      const response = await axios.delete(
        `${API}/dispatch/custom-units/${unit.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Unit deactivated');
        fetchUnits();
      } else {
        toast.error(response.data.error || 'Failed to deactivate');
      }
    } catch (error) {
      console.error('Failed to delete unit:', error);
      toast.error('Failed to deactivate unit');
    }
  };

  const openAddDialog = () => {
    setEditingUnit(null);
    setFormData({
      unit_number: '',
      description: '',
      owner: 'Rogers'
    });
    setShowDialog(true);
  };

  const openEditDialog = (unit) => {
    setEditingUnit(unit);
    setFormData({
      unit_number: unit.unit_number,
      description: unit.description || '',
      owner: unit.owner || 'Rogers'
    });
    setShowDialog(true);
  };

  // Filter units based on search
  const filteredUnits = units.filter(u => 
    u.unit_number?.toLowerCase().includes(search.toLowerCase()) ||
    u.description?.toLowerCase().includes(search.toLowerCase()) ||
    u.owner?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="custom-units-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Truck className="h-5 w-5 text-purple-500" />
            Units
          </h1>
          <div className="flex gap-1">
            <Link to="/dashboard/dispatch">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <ClipboardList className="h-4 w-4 mr-1" />
                Dispatch Board
              </Button>
            </Link>
            <Link to="/dashboard/dispatch/jobs">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <Briefcase className="h-4 w-4 mr-1" />
                Jobs
              </Button>
            </Link>
            <Link to="/dashboard/dispatch/drivers">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <Users className="h-4 w-4 mr-1" />
                Operators
              </Button>
            </Link>
            <Link to="/dashboard/dispatch/units">
              <Button variant="ghost" size="sm" className="bg-secondary text-foreground">
                <Truck className="h-4 w-4 mr-1" />
                Units
              </Button>
            </Link>
            <Link to="/dashboard/dispatch/invoice-review">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <DollarSign className="h-4 w-4 mr-1" />
                Invoice Review
              </Button>
            </Link>
          </div>
        </div>
        <Button onClick={openAddDialog} data-testid="add-unit-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Unit
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Units ({filteredUnits.length})</CardTitle>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search units..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                Show inactive
              </label>
              <Button variant="outline" size="sm" onClick={fetchUnits}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredUnits.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {search ? 'No units match your search' : 'No units found. Add your first unit!'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Unit #</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUnits.map(unit => (
                  <TableRow key={unit.id} data-testid={`unit-row-${unit.id}`}>
                    <TableCell className="font-medium">{unit.unit_number}</TableCell>
                    <TableCell>{unit.description || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={unit.owner === 'Rogers' ? 'default' : 'secondary'}>
                        {unit.owner}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={unit.active ? 'success' : 'destructive'}>
                        {unit.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(unit)}
                          data-testid={`edit-unit-${unit.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {unit.active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(unit)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-unit-${unit.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUnit ? 'Edit Unit' : 'Add New Unit'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Unit Number *</label>
              <Input
                value={formData.unit_number}
                onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                placeholder="e.g., 946, 1A-JAIMIE"
                required
                data-testid="unit-number-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Stacker, Tractor Truck"
                data-testid="unit-description-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Owner *</label>
              <Select
                value={formData.owner}
                onValueChange={(value) => setFormData({ ...formData, owner: value })}
              >
                <SelectTrigger data-testid="unit-owner-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Rogers">Rogers</SelectItem>
                  <SelectItem value="Contractor">Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-unit-btn">
                {saving ? 'Saving...' : editingUnit ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
